import values from 'lodash/values';
import reduce from 'lodash/reduce';
import { css } from 'styled-components';
import { EElementType, INormalizedLandingElement } from '~/src/services/projects';
import { CSSPropWithBreakpoints } from '~/src/components/LandingBlock/styled';
import { ILandingBlock } from '~/src/components/LandingBlock';

export interface IGridItem {
    id?: string;
    key: string;
    src: string;
    webp: string;
    alt: string;
    customStyles: {
        Image: CSSPropWithBreakpoints;
        Text: CSSPropWithBreakpoints;
        SubTitle?: CSSPropWithBreakpoints;
        Title?: CSSPropWithBreakpoints;
    };
    backgroundColor?: string;
    padding?: {
        vertical?: number;
        horizontal?: number
    }
    borderRadius?: number;
    sortOrder?: number;
    width?: number;
}

export interface ITabItem{
    [key: string]: any;
}

export interface ILigalItem {
    key: string;
    customStyles: {
        Text: CSSPropWithBreakpoints;
    };
    sortOrder?: number;
}

export const prepareColumnsToPreview = (columns: INormalizedLandingElement, isAdmin = false, currentBreakpoint?: string): IGridItem[] => {
    const result: IGridItem[] = values(reduce(columns, (acc, item) => {
        if (item.title === 'Header') return acc;
        const itemCustomStyles = prepareCustomStyles(item, item.title,  isAdmin, currentBreakpoint);
        switch (item.type) {
            case EElementType.Column:
            case EElementType.HeaderColumn:
            case EElementType.ImageGridColumn:
            case EElementType.BlockGridColumn:
            case EElementType.AccordionColumn:
            case EElementType.ImageSection:
            case EElementType.Button:
                acc[item.id] = {
                    ...acc[item.id],
                    customStyles: {},
                    key: item.id,
                    sortOrder: item.properties ? +item.properties['sortOrder'] : undefined,
                    backgroundColor: item.properties ? item.properties['backgroundColor'] : undefined,
                    padding: {
                        horizontal: item.properties ? item.properties['paddingHorizontal'] ?? 0 : 0,
                        vertical: item.properties ? item.properties['paddingVertical'] ?? 0 : 0,
                    },
                    borderRadius: item.properties ? +item.properties['borderRadius'] : 0,
                    defaultOpen: item.properties ? item.properties['defaultOpen'] : false,
                    width: item.properties ? item.properties['width'] : 4,
                };
                break;
            case EElementType.LigalColumn:
                acc[item.id] = {
                    ...acc[item.id],
                    customStyles: {
                        ...acc[item.parentId]?.customStyles,
                        ...itemCustomStyles,
                    },
                    key: item.id,
                    sortOrder: item.properties ? +item.properties['sortOrder'] : undefined,
                };
                break;
            case EElementType.Image:
                acc[item.parentId] = {
                    ...acc[item.parentId],
                    src: item.properties.content,
                    webp: item.properties.webp,
                    alt: item.properties.alt,
                    customStyles: {
                        ...acc[item.parentId]?.customStyles,
                        ...itemCustomStyles,
                    },
                };
                break;
            case EElementType.Text:
                if (acc[item.parentId] !== undefined) {
                    acc[item.parentId] = {
                        ...acc[item.parentId],
                        [item.title?.toLowerCase()]: item.properties.content,
                        customStyles: {
                            ...acc[item.parentId].customStyles,
                            ...itemCustomStyles,
                        },
                    };
                } else {
                    acc[item.id] = {
                        ...acc[item.id],
                        customStyles: { ...itemCustomStyles },
                        key: item.id,
                        sortOrder: item.properties ? +item.properties['sortOrder'] : undefined,
                    };
                }
                break;
            case EElementType.RTE:
                acc[item.parentId] = {
                    ...acc[item.parentId],
                    [item.title?.toLowerCase()]: item.properties.htmlContent,
                    customStyles: {
                        ...acc[item.parentId].customStyles,
                        ...itemCustomStyles,
                    },
                };
                break;
        }
        return acc;
    }, {}));
    return result.sort((a, b) => a.sortOrder - b.sortOrder);
};

export const prepareTabsToPreview = (tabs: INormalizedLandingElement, isAdmin = false, currentBreakpoint?: string): ITabItem[] => {
    // @ts-ignore
    const result: ITabItem[] = reduce(tabs, (acc, item) => {
        if (item.title === 'Header') return acc;
        const itemCustomStyles = prepareCustomStyles(item, item.title,  isAdmin, currentBreakpoint);
        switch (item.type) {
            case EElementType.TabColumn:
                acc[item.id] = {
                    ...acc[item.id],
                    customStyles: {},
                    key: item.id,
                    type: item.type,
                    sortOrder: item.properties ? +item.properties['sortOrder'] : undefined,
                };
                break;
            case EElementType.Image:
                acc[item.parentId] = {
                    ...acc[item.parentId],
                    src: item.properties.content,
                    webp: item.properties.webp,
                    alt: item.properties.alt,
                    customStyles: {
                        ...acc[item.parentId]?.customStyles,
                        ...itemCustomStyles,
                    },
                };
                break;
            case EElementType.Text:
                acc[item.parentId] = {
                    ...acc[item.parentId],
                    [item.title?.toLowerCase()]: item.properties.content,
                    customStyles: {
                        ...acc[item.parentId].customStyles,
                        ...itemCustomStyles,
                    },
                };
                break;
            case EElementType.RTE:
                acc[item.parentId] = {
                    ...acc[item.parentId],
                    [item.title?.toLowerCase()]: item.properties.htmlContent,
                    customStyles: {
                        ...acc[item.parentId].customStyles,
                        ...itemCustomStyles,
                    },
                };
                break;
            case EElementType.Button:
                acc[item.id] = {
                    ...acc[item.id],
                    customStyles: {},
                    key: item.id,
                    sortOrder: item.properties ? +item.properties['sortOrder'] : undefined,
                };
                break;
        }
        return acc;
    }, {});
    return result;
};

export interface IPreparedBlock extends ILandingBlock {
    key: string;
    customStyles: any;
}

export const prepareImagesToPreview = (columns: INormalizedLandingElement, isAdmin = false, currentBreakpoint?: string): IGridItem[] => {
    const result: IGridItem[] = values(reduce(columns, (acc, item) => {
        const itemCustomStyles = prepareCustomStyles(item, item.title,  isAdmin, currentBreakpoint);
        switch (item.type) {
            case EElementType.Column:
            case EElementType.CarouselImage:
                acc[item.id] = {
                    ...acc[item.id],
                    customStyles: {},
                    key: item.id,
                    sortOrder: item.properties ? +item.properties['sortOrder'] : undefined,
                };
                break;
            case EElementType.Image:
                acc[item.parentId] = {
                    ...acc[item.parentId],
                    src: item.properties.content,
                    webp: item.properties.webp,
                    alt: item.properties.alt,
                    customStyles: {
                        ...acc[item.parentId].customStyles,
                        ...itemCustomStyles,
                    },
                };
                break;
        }
        return acc;
    }, {}));
    return result.sort((a, b) => a.sortOrder - b.sortOrder);
};

export const prepareBlock = (body: INormalizedLandingElement, isAdmin = false, currentBreakpoint?: string): IPreparedBlock[] => {
    const result: IPreparedBlock[] = [];
    // Связку id блока с индексом в результирующем массиве
    const cachedIndex: Record<string, number> = {};

    // Цикл критичный по производительности, поэтому используем только нативные циклы и заполняем cachedIndex
    // Вся обработка структуры проходит за O(n)
    for (const key in body) {
        const item = body[key];
        const blockIndex = cachedIndex[item.blockId];

        const itemCustomStyles = prepareCustomStyles(item, item.renderId, isAdmin, currentBreakpoint);

        if (item.type === EElementType.Block) {
            if (!blockIndex) {
                result.push({
                    key: item.id,
                    block: item,
                    items: {},
                    customStyles: { ...itemCustomStyles },
                    enableWebp: true,
                });
                cachedIndex[item.id] = result.length - 1;
            } else {
                result[blockIndex] = {
                    ...result[blockIndex],
                    block: item,
                    customStyles: { ...result[blockIndex].customStyles, ...itemCustomStyles },
                };
            }
        } else {
            if (!result[blockIndex]) {
                result.push({
                    key: item.blockId,
                    block: {},
                    items: { [item.id]: item },
                    customStyles: { ...itemCustomStyles },
                    enableWebp: true,
                });
                cachedIndex[item.blockId] = result.length - 1;
            }
            result[blockIndex] = {
                ...result[blockIndex],
                items: { ...result[blockIndex]?.items, [item.id]: item },
                customStyles: { ...result[blockIndex]?.customStyles, ...itemCustomStyles },
            };
        }
    }

    // Поскольку количество блоков на странице не большое, можем сделать сортировку стандартным методом
    // и не переусложнять логику цикла, который формирует данные
    return result.sort((a, b) => a.block.properties['sortOrder'] - b.block.properties['sortOrder']);
};

export const prepareCustomStyles = (item, key, isAdmin, currentBreakpoint) => {
    return {
        [key]: {
            desktop: css`
                      ${item.properties?.customize}
                      ${item.properties?.customStyles?.desktop}
                    `,
            sDesktop: css`
                      ${item.properties?.customStyles?.sDesktop}
                    `,
            tablet: css`
                      ${item.properties?.customStyles?.tablet}
                    `,
            mobile: css`
                      ${item.properties?.customStyles?.mobile}
                    `,
            meta: isAdmin
                ? {
                    isAdmin,
                    currentBreakpoint,
                }
                : null,
        },
    };
};
